"use client";

import { useCallback, useEffect, useState } from "react";
import Loading from "@/app/components/Loading";
import { useGateHold } from "@/app/components/Gate";
import BattleDemoBattle from "./BattleDemoBattle";
import { BATTLE_DEMO_CSS } from "./battleDemoStyle";
import { BattleData, Plan } from "./battleDemoTypes";

const WORLD = "Baunheim";

interface Flags {
  in_battle: boolean;
}

/**
 * 戦闘の一時画面。進行の画面から、戦闘中に入力シートのバーを押して開く。
 * 中身の画面は戦闘デモプレイと同じ部品（play の形）。データは /api/play/battle と battle_turn から取る
 */
export default function PlayBattle({
  region,
  slug,
  onExit,
}: {
  region: string;
  slug: string;
  /** 物語の画面へ戻る。戦闘が終わっていれば閉じてから戻る */
  onExit: (flags?: Flags) => void;
}) {
  const query = `world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}`;
  const [data, setData] = useState<BattleData | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  // 戦闘の中身を読み終えるまで、ロード画面に待ってもらう
  useGateHold(data === null && message === "");

  const load = useCallback(async () => {
    try {
      const response = await fetch(`/api/play/battle?${query}`);
      const body = await response.json();
      if (!response.ok) {
        setMessage(body.error ?? "戦闘を読めません");
        return;
      }
      setData(body.battle as BattleData);
    } catch {
      setMessage("サーバーに繋がりません");
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  // 組の戦闘で、ほかの PC の行動を待っている間は、4 秒ごとに読み直す
  const waiting = (data?.view as { waiting?: string[] } | undefined)?.waiting ?? [];
  const waitingNow = !!data && !data.view.result && data.view.awaiting.length === 0 && waiting.length > 0;
  useEffect(() => {
    if (!waitingNow) return;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [waitingNow, load]);

  const post = async (op: "battle_turn" | "battle_leave", body: Record<string, unknown>) => {
    const response = await fetch(`/api/play/${op}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ world: WORLD, region, slug, ...body }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error ?? "送れません");
    return result as { ok: boolean; flags: Flags; battle?: BattleData };
  };

  const submit = async (plans: Record<string, Plan>) => {
    setBusy(true);
    setMessage("");
    try {
      const result = await post("battle_turn", { plans });
      if (result.battle) setData(result.battle);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "行動を送れません");
    } finally {
      setBusy(false);
    }
  };

  /** 結末が付いていれば閉じて戻る。付いていなければそのまま戻る（戦闘は残る） */
  const exit = async () => {
    if (data?.view.result) {
      setBusy(true);
      try {
        const result = await post("battle_leave", {});
        onExit(result.flags);
        return;
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "閉じられません");
      } finally {
        setBusy(false);
      }
    }
    onExit();
  };

  return (
    <div className="bd-root">
      <style>{BATTLE_DEMO_CSS}</style>
      <div className="bd-app">
        {data ? (
          <BattleDemoBattle data={data} busy={busy} last={null} over={false} onSubmit={submit} onNext={exit} onTop={exit} onAbandon={exit} mode="play" />
        ) : (
          <div className="bd-page" style={{ padding: 24 }}>
            {message ? <p>{message}</p> : <Loading label="戦闘を読んでいます" />}
            {message ? (
              <button className="bd-btn" onClick={() => onExit()}>
                物語の画面へ戻る
              </button>
            ) : null}
          </div>
        )}
        {waitingNow ? <p style={{ padding: "0 16px" }}>{waiting.join("・")} の行動を待っています</p> : null}
        {message && data ? <p style={{ padding: "0 16px", color: "#e26a5a" }}>{message}</p> : null}
      </div>
    </div>
  );
}
